// Full updated Carousel with internal selectedIndex tracking and data-state for inactive items
'use client';

import * as React from 'react';

import type { EmblaPluginType } from 'embla-carousel';
import Autoplay, { type AutoplayType } from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  autoPlay?: boolean;
  transition?: 'slide' | 'fade';
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) throw new Error('useCarousel must be used within a <Carousel />');
  return context;
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      autoPlay,
      transition = 'slide',
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const activePlugins: EmblaPluginType[] = [WheelGesturesPlugin()];
    if (autoPlay) activePlugins.push(Autoplay({ delay: 5000 }));
    if (transition === 'fade') activePlugins.push(Fade());
    if (plugins) activePlugins.push(...plugins);

    const [carouselRef, api] = useEmblaCarousel(
      { ...opts, axis: orientation === 'horizontal' ? 'x' : 'y' },
      activePlugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) return;
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setSelectedIndex(api.selectedScrollSnap());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
      const autoplay = api?.plugins()?.autoplay as AutoplayType | undefined;
      if (autoplay) (autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop)();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
      const autoplay = api?.plugins()?.autoplay as AutoplayType | undefined;
      if (autoplay) (autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop)();
    }, [api]);

    React.useEffect(() => {
      if (!api) return;
      if (setApi) setApi(api);
      onSelect(api);
      api.on('select', onSelect).on('reInit', onSelect);
      return () => {
        api.off('select', onSelect);
        api.off('reInit', onSelect);
      };
    }, [api, setApi, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          api,
          canScrollNext,
          canScrollPrev,
          carouselRef,
          opts,
          orientation,
          scrollNext,
          scrollPrev,
          selectedIndex,
        }}
      >
        <div
          aria-roledescription='carousel'
          className={cn('group/carousel relative', className)}
          ref={ref}
          role='region'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    viewportClassName?: string;
  }
>(({ className, viewportClassName, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();
  return (
    <div ref={carouselRef} className={viewportClassName}>
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' && '-ml-5',
          orientation === 'vertical' && '-mt-5 flex-col',
          className,
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { index: number; dimIfInactive?: boolean }
>(({ className, index, dimIfInactive = false, ...props }, ref) => {
  const { orientation, selectedIndex } = useCarousel();
  return (
    <div
      aria-roledescription='slide'
      data-state={selectedIndex === index ? 'active' : 'inactive'}
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full transition-opacity duration-300',
        selectedIndex !== index && dimIfInactive && 'opacity-50',
        orientation === 'horizontal' && 'pl-5',
        orientation === 'vertical' && 'pt-5',
        className,
      )}
      ref={ref}
      role='group'
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

type CarouselPreviousProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'ghost';
  size?: 'default' | 'lg';
  position?: 'default' | 'overflow';
};

const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselPreviousProps>(({ className, ...props }, ref) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      className={cn('bg-ultramarine  size-10  rounded-full p-2 text-white', className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      ref={ref}
      {...props}
    >
      <ChevronLeft className='size-6' strokeWidth={3} />
      <span className='sr-only'>Previous slide</span>
    </button>
  );
});
CarouselPrevious.displayName = 'CarouselPrevious';

type CarouselNextProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'ghost';
  size?: 'default' | 'lg';
  position?: 'default' | 'overflow';
};

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNextProps>(({ className, ...props }, ref) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      className={cn('bg-ultramarine size-10 rounded-full p-2 text-white', className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      ref={ref}
      {...props}
    >
      <ChevronRight className='size-6' strokeWidth={3} />
      <span className='sr-only'>Next slide</span>
    </button>
  );
});
CarouselNext.displayName = 'CarouselNext';

const CarouselDots = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { api, selectedIndex } = useCarousel();

    if (!api) return null;

    const totalItems = api.scrollSnapList().length;

    return (
      <div ref={ref} className={cn('flex gap-x-3', className)} {...props}>
        {Array.from({ length: totalItems }).map((_, index) => (
          <button
            type='button'
            key={index}
            onClick={() => api.scrollTo(index)}
            className={cn(' size-3 rounded-full', selectedIndex === index ? 'bg-ultramarine' : 'bg-ultramarine/10')}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    );
  },
);
CarouselDots.displayName = 'CarouselDots';

export { Carousel, CarouselContent, CarouselDots, CarouselItem, CarouselPrevious, CarouselNext, useCarousel };
